import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { LinkInfoCircuit } from "../../wizard/steps/4-links/links-step.model";
import {
    Amplifier,
    DwdmAmplifierLink,
    DwdmCircuitAmplifierProfileData
} from "../../wizard/steps/5-amplifiers/amplifiers-step.model";
import {
    AmplifierDirectionMap,
    AmplifierElement,
    AmplifierType,
    CircuitElementState
} from "../models/diagram.models";
import { DwdmDiagramConverterHelper } from "./dwdm-diagram-converter-helper";
import { AmplifiersConverterData, LinkEnd } from "./dwdm-diagram-converter.models";

/**
 * /**
 * Classe abstrata para realizar a conversão de @see AmplifiersConverterData em @see AmplifierDirectionMap.
 *
 * Essa classe contém todos os comportamentos comuns entre todas as janelas.
 * Comportamentos específicos da tela de Diagnósticos ou da Wizard de criação devem ser
 * implementados nas classes concretas.
 */
export abstract class DwdmDiagramAbstractConverterAmplifiers {

    private readonly EDFA_PORT_MODEL = "edfa";

    protected convertData(amplifiersData: AmplifiersConverterData,
            amplifierBuilder: (amplifier: Amplifier, amplifierData: any) => AmplifierElement): AmplifierDirectionMap {
        let amplifiersMap: AmplifierDirectionMap = {};
        const amplifiers = amplifiersData.amplifiers;
        const links = amplifiersData.links;

        if (amplifiers) {
            // Se há "amplifiers", então já passamos pelo step de amplificadores
            // e possuimos todos os dados para criá-los com todas as informações.
            amplifiersMap = this.buildConfiguredAmplifiers(amplifiersData, amplifiers, links, amplifierBuilder);
        } else if (links) {
            // Se não há "amplifiers" mas há links, então estamos no step de links
            // e precisamos criar amplificadores com estado inicial.
            amplifiersMap = this.buildAmplifiersFromLinks(amplifiersData, links, amplifierBuilder);
        }

        return Object.entries(amplifiersMap).length > 0 ? amplifiersMap : null;
    }

    private buildConfiguredAmplifiers(amplifiersData: AmplifiersConverterData, amplifiers: Amplifier[],
            links: LinkInfoCircuit[], amplifierBuilder: (amplifier: Amplifier, amplifierData: any) => AmplifierElement) {

        const amplifiersMap: AmplifierDirectionMap = {};
        const deviceResourceId = amplifiersData.deviceResourceId;
        const deviceAmplifiers = this.findAmplifiersForDevice(amplifiers, deviceResourceId);

        for (const amplifier of deviceAmplifiers) {
            const amplifierLinks = this.findAmplifierLinks(links, amplifier.links);

            for (const link of amplifierLinks) {
                const amplifierData = this.getAmplifierData(amplifiersData, amplifier, link);

                if (amplifierData) {
                    amplifiersMap[amplifierData.direction] = amplifierBuilder(amplifier, amplifierData);
                }
            }
        }

        return amplifiersMap;
    }

    private buildAmplifiersFromLinks(amplifiersData: AmplifiersConverterData, links: LinkInfoCircuit[],
            amplifierBuilder: (amplifier: Amplifier, amplifierData: any) => AmplifierElement) {

        const amplifiersMap: AmplifierDirectionMap = {};
        const deviceResourceId = amplifiersData.deviceResourceId;
        const deviceLinks = this.getLinksFromEnd(deviceResourceId, links);

        for (const link of deviceLinks) {
            const configuredAmplifier = null;
            const amplifierData = this.getAmplifierData(amplifiersData, configuredAmplifier, link);

            if (amplifierData) {
                amplifiersMap[amplifierData.direction] = amplifierBuilder(configuredAmplifier, amplifierData);
            }
        }

        return amplifiersMap;
    }

    private getLinksFromEnd(deviceResourceId: number, links: LinkInfoCircuit[]) {
        return links.filter(link => link.aEndResourceId === deviceResourceId || link.zEndResourceId === deviceResourceId);
    }

    private getAmplifierData(amplifiersData: AmplifiersConverterData, configuredAmplifier: Amplifier,
            link: LinkInfoCircuit) {

        const linkEndWithAmplifier = this.getLinkEndWithAmplifierForDevice(link, amplifiersData.deviceResourceId);

        if (!linkEndWithAmplifier && !configuredAmplifier) {
            return null;
        }

        const circuitAEnd = amplifiersData.circuitAEnd;
        const circuitZEnd = amplifiersData.circuitZEnd;
        const intermediates = amplifiersData.intermediates;

        // A direção do amplificador depende da direção do link
        const direction = DwdmDiagramConverterHelper.getLinkDirection(link, circuitAEnd.id, circuitZEnd.id, intermediates);

        const name = configuredAmplifier ? configuredAmplifier.identifier : "";
        const amplifierType = this.getType(linkEndWithAmplifier, circuitAEnd, circuitZEnd, configuredAmplifier);
        const state = this.getState(configuredAmplifier);

        return { direction, amplifierType, state, name };
    }

    /**
     * Encontra todos os amplificadores associados ao device que está sendo desenhado no momento.
     */
    private findAmplifiersForDevice(amplifiers: Amplifier[], deviceResourceId: number): Amplifier[] {
        return amplifiers.filter(amplifier => amplifier.portIdentifier.deviceResourceId === deviceResourceId);
    }

    /**
     * Busca os links selecionados no circuito ligados aos amplificadores.
     *
     * @param links - Links selecionados no circuito.
     * @param amplifierLinks - Links dos amplificadores.
     * @returns Lista de links dos amplificadores
     * @see LinkInfoCircuit
     */
    private findAmplifierLinks(links: LinkInfoCircuit[], amplifierLinks: DwdmAmplifierLink[]): LinkInfoCircuit[] {
        const amplifierLinksIds = amplifierLinks.map(amplifierLink => amplifierLink.linkId);

        return links.filter(link => amplifierLinksIds.includes(link.id));
    }

    private getLinkEndWithAmplifierForDevice(link: LinkInfoCircuit, deviceResourceId: number): LinkEnd | null {
        let linkEnd: LinkEnd = null;

        if (link.aEndResourceId === deviceResourceId && this.isEdfaPort(link.aEndPortModel)) {
            linkEnd = {
                endResourceId: link.aEndResourceId,
                transmission: DwdmDiagramConverterHelper.getTransmission(link.aEndTransmission),
                endName: link.aEnd,
                endPortModel: link.aEndPortModel,
                linkName: link.name
            };
        } else if (link.zEndResourceId === deviceResourceId && this.isEdfaPort(link.zEndPortModel)) {
            linkEnd = {
                endResourceId: link.zEndResourceId,
                transmission: DwdmDiagramConverterHelper.getTransmission(link.zEndTransmission),
                endName: link.zEnd,
                endPortModel: link.zEndPortModel,
                linkName: link.name
            };
        }

        return linkEnd;
    }

    /**
     * O tipo do amplificador é definido pela seguinte lógica:
     *     Amplificadores não configurados, que estiverem em algum dos ends do
     *     circuito com a transmissão TX, tem o tipo BOOSTER; caso a transmissão
     *     seja RX, o tipo é PRE_AMP; e caso que estiverem nos equipamentos
     *     intermediários, o tipo é BOOSTER, independente da transmissão.
     *     Amplificadores que já foram configurados assumem o tipo de acordo
     *     com a seleção do usuário.
     */
    private getType(linkEnd: LinkEnd, circuitAEnd: Device, circuitZEnd: Device, amplifier?: Amplifier): AmplifierType {
        if (amplifier) {
            return amplifier.profile === DwdmCircuitAmplifierProfileData.BOOSTER ? AmplifierType.BOOSTER : AmplifierType.PRE_AMP;
        }

        if (this.isCircuitEnd(linkEnd, circuitAEnd, circuitZEnd)) {
            return linkEnd.transmission === Transmission.TX ? AmplifierType.BOOSTER : AmplifierType.PRE_AMP;
        }

        return AmplifierType.BOOSTER;
    }

    /**
     * Quando ainda não existe um amplificador, significa que estamos no step de links, dessa forma o estado do Amplificador
     * será inicial, ou seja, @see CircuitElementState.INITIAL.
     * A partir do momento em que esse amplificador já existe, ou seja, já passamos pelo step de amplificadores, o estado reflete
     * se o amplificador já foi configurado ou não.
     */
    private getState(amplifier?: Amplifier) {
        if (amplifier) {
            return this.isConfiguredAmplifier(amplifier) ? CircuitElementState.CONFIGURED : CircuitElementState.NOT_CONFIGURED;
        }

        return CircuitElementState.INITIAL;
    }

    /**
     * Verifica se o end de um link é um end do circuito.
     */
    private isCircuitEnd(linkEnd: LinkEnd, circuitAEnd: Device, circuitZEnd: Device): boolean {
        return linkEnd.endResourceId === circuitAEnd.id || linkEnd.endResourceId === circuitZEnd.id;
    }

    /**
     * Verifica se o modelo recebido é de uma porta amplificadora.
     *
     * FIXME US-36839
     */
    private isEdfaPort(portModel: string): boolean {
        return portModel === this.EDFA_PORT_MODEL;
    }

    /**
     * Verifica se o amplificador já foi configurado pelo usuário.
     *
     * O 'mode', do amplifier, só é setado pela modal de configuração.
     * Logo, se ele for nulo é porque ainda não foi configurado.
     *
     * Ao clicar no botão que limpa as configurações do amplificador, esse atributo volta a ser nulo.
     */
    private isConfiguredAmplifier(amplifier: Amplifier): boolean {
        return amplifier.mode ? true : false;
    }
}
